/** The itinerary record's section registry (plan §5 "Cards", §9.5 "Spec enums for the VIEW
 *  halves", §10.3; O10): every key the record shows → its card (Catalog entry · App button ·
 *  Days · Content · Visibility & search · Access · Identity & source), its position in the
 *  card, its block (the Editable fields, the muted "From Branch.io" block of the deep link and
 *  its QR code, or the System block of derived and provenance values), its label and its
 *  widget. One entity, `itinerary:<group id>`, carries the group's fields and its default
 *  itinerary's alike; a day's own fields are `day:<id>` keys (`ITINERARY_DAY_REGISTRY`).
 *
 *  The widget names the edit-mode input the EDIT half mounts and, through it, the read view
 *  the VIEW half shows where a value has a shape (the rich-text box) — so pressing E moves
 *  nothing. Labels, kinds, widgets and choices mirror the backend's catalog
 *  (`apiv2/lib/console/itinerary_changeset/fields.py`): the VIEW half renders from the detail
 *  alone, before any catalog is read.
 *
 *  The same shape as the activity, booking, order and user registries (`RegistryEntry`), so
 *  the records core lays all of them out alike; an itinerary has no lifecycle, so `stage` and
 *  `facet` are the card.
 *
 *  Contract (registry.contract.test.ts): the keys are exactly the spec enum
 *  `ItineraryRecordFieldKey`, in its order; the editable ones exactly
 *  `ItineraryRecordEditableFieldKey`; the cards exactly `ItineraryRecordSection`; the day
 *  keys exactly `ItineraryDayRecordFieldKey`. The maps are typed by the generated enums as
 *  well, so a regenerated `types.ts` that adds or drops a key fails the typecheck before the
 *  test runs. */
import { CTA_BUTTON_BEHAVIOR_LABELS, CTA_TOUR_DEPARTURE_LABELS } from '@/api/itineraries';
import {
  ITINERARY_RECORD_SECTIONS,
  type ItineraryDayRecordFieldKey,
  type ItineraryRecordFieldKey,
  type ItineraryRecordSection,
} from '@/api/itineraryRecord';
import { coreWidget } from '@/features/catalog/recordFields';
import type { RecordChoice, RecordField, RecordFieldKind } from '@/features/records/editor/types';
import type { FieldBlockKind, RegistryEntry } from '@/features/records/views/types';

/** The itinerary changes' `entity` (`itinerary:<group id>`). */
export const ITINERARY_ENTITY = 'itinerary';

/** A day's changes' `entity` (`day:<id>`, a negative id for a day the draft adds). */
export const ITINERARY_DAY_ENTITY = 'day';

/** A field's kind in the backend catalog's terms: `editable` — an input in edit mode;
 *  `vendor` — made by Branch.io (the deep link and its QR code), read-only; `derived` —
 *  system or provenance, read-only. */
export type ItineraryFieldKind = Extract<RecordFieldKind, 'editable' | 'vendor' | 'derived'>;

export interface ItineraryRegistryEntry extends RegistryEntry<ItineraryRecordSection, ItineraryRecordSection> {
  label: string;
  kind: ItineraryFieldKind;
  /** The edit-mode input (and its read view); null for the read-only kinds. */
  widget: string | null;
  /** An enum widget's values, as stored. */
  choices?: readonly RecordChoice[];
}

const BLOCK_OF: Readonly<Record<ItineraryFieldKind, FieldBlockKind>> = {
  editable: 'editable',
  vendor: 'vendor',
  derived: 'system',
};

function entry(
  section: ItineraryRecordSection,
  order: number,
  kind: ItineraryFieldKind,
  label: string,
  widget: string | null = null,
  choices?: readonly RecordChoice[],
): ItineraryRegistryEntry {
  return {
    stage: section,
    facet: section,
    order,
    block: BLOCK_OF[kind],
    label,
    kind,
    widget,
    ...(choices ? { choices } : {}),
  };
}

const edit = (
  section: ItineraryRecordSection,
  order: number,
  label: string,
  widget: string,
  choices?: readonly RecordChoice[],
) => entry(section, order, 'editable', label, widget, choices);
const fromBranch = (section: ItineraryRecordSection, order: number, label: string) =>
  entry(section, order, 'vendor', label);
const system = (section: ItineraryRecordSection, order: number, label: string) =>
  entry(section, order, 'derived', label);

const fromLabels = (labels: Readonly<Record<number, string>>): RecordChoice[] =>
  Object.entries(labels)
    .map(([value, label]) => ({ value: Number(value), label }))
    .sort((a, b) => a.value - b.value);

/** `Itinerary.DISPLAY_CURRENCIES` in the backend catalog's words (the spec's
 *  `x-enum-labels`). */
const DISPLAY_CURRENCY_CHOICES = fromLabels({
  1: 'Tour operator default',
  2: 'Display prices in USD',
  3: 'Display prices in Cost Currency',
  4: 'Display prices in local currency',
});

export const ITINERARY_RECORD_REGISTRY = {
  // ----------------------------------------------------------- Catalog entry
  name: edit('catalog_entry', 10, 'Name', 'text'),
  description: edit('catalog_entry', 20, 'Synopsis', 'textarea'),
  duration_display: edit('catalog_entry', 30, 'Duration display', 'text'),
  cost: edit('catalog_entry', 40, 'Price', 'number'),
  cost_prefix: edit('catalog_entry', 50, 'Price prefix', 'text'),
  cost_unit: edit('catalog_entry', 60, 'Price unit', 'text'),
  currency: edit('catalog_entry', 70, 'Currency', 'currency'),
  catalog_display_currency: edit('catalog_entry', 80, 'Catalog display currency', 'enum', DISPLAY_CURRENCY_CHOICES),
  ontrip_display_currency: edit('catalog_entry', 90, 'On-trip display currency', 'enum', DISPLAY_CURRENCY_CHOICES),
  cover_image: system('catalog_entry', 100, 'Cover'),

  // -------------------------------------------------------------- App button
  cta_button_behavior: edit('app_button', 10, 'Button behaviour', 'enum', fromLabels(CTA_BUTTON_BEHAVIOR_LABELS)),
  cta_button_text: edit('app_button', 20, 'Button text', 'text'),
  booking_url: edit('app_button', 30, 'Booking URL', 'url'),
  cta_tour: edit('app_button', 40, 'Tour', 'tour'),
  cta_tour_departure: edit('app_button', 50, 'Departure', 'enum', fromLabels(CTA_TOUR_DEPARTURE_LABELS)),
  operator_cta_default: system('app_button', 60, "Operator's button behaviour"),

  // -------------------------------------------------------------------- Days
  days: edit('days', 10, 'Days', 'days'),

  // ----------------------------------------------------------------- Content
  highlights: edit('content', 10, 'Highlights', 'rich_text'),
  notes: edit('content', 20, 'Notes', 'rich_text'),
  packing_tips: edit('content', 30, 'Packing tips', 'rich_text'),
  footer: edit('content', 40, 'Footer', 'rich_text'),
  route_map: edit('content', 50, 'Route map', 'image'),

  // ----------------------------------------------------- Visibility & search
  published: edit('visibility', 10, 'Published', 'boolean'),
  show_in_catalog: edit('visibility', 20, 'In the catalog', 'boolean'),
  show_in_region_search: edit('visibility', 30, 'Region search', 'boolean'),
  show_in_trip_type_search: edit('visibility', 40, 'Trip type search', 'boolean'),
  show_in_text_search: edit('visibility', 50, 'Text search', 'boolean'),
  custom_day_titles: edit('visibility', 60, 'Custom day titles', 'boolean'),
  sequence: edit('visibility', 70, 'Catalog order', 'integer'),
  months: edit('visibility', 80, 'Months', 'months'),
  regions: edit('visibility', 90, 'Regions', 'regions'),
  trip_types: edit('visibility', 100, 'Filters', 'trip_types'),
  tags: edit('visibility', 110, 'Catalog search tags', 'tags'),

  // ------------------------------------------------------------------ Access
  atag: edit('access', 10, 'Access tags', 'atags'),

  // ------------------------------------------------------- Identity & source
  custom_id: edit('identity', 10, 'Custom ID', 'text'),
  vehicle_route_set: edit('identity', 20, 'Line set', 'line_set'),
  ext_id: system('identity', 30, 'Import identity'),
  branchio_link: fromBranch('identity', 40, 'Deep link'),
  qr_image_url: fromBranch('identity', 50, 'QR code'),
  source: system('identity', 60, 'Source'),
  default_itinerary: system('identity', 70, 'Catalog itinerary'),
} as const satisfies Record<ItineraryRecordFieldKey, ItineraryRegistryEntry>;

export type ItineraryRegistry = Readonly<Record<ItineraryRecordFieldKey, ItineraryRegistryEntry>>;

/** Card titles, in page order. */
export const ITINERARY_SECTION_TITLES: Readonly<Record<ItineraryRecordSection, string>> = {
  catalog_entry: 'Catalog entry',
  app_button: 'App button',
  days: 'Days',
  content: 'Content',
  visibility: 'Visibility & search',
  access: 'Access',
  identity: 'Identity & source',
};

/** A day's field (`day:<id>`): every one editable, all on the Days card's open day. */
export interface ItineraryDayRegistryEntry {
  order: number;
  label: string;
  widget: string;
}

/** A day's keys, in `ItineraryDayRecordFieldKey` order — the Days card's open day. */
export const ITINERARY_DAY_REGISTRY = {
  title: { order: 10, label: 'Title', widget: 'text' },
  subtitle: { order: 20, label: 'Subtitle', widget: 'text' },
  day_span: { order: 30, label: 'Days', widget: 'integer' },
  image: { order: 40, label: 'Image', widget: 'image' },
  description: { order: 50, label: 'Description', widget: 'rich_text' },
  footer: { order: 60, label: 'Footer', widget: 'rich_text' },
} as const satisfies Record<ItineraryDayRecordFieldKey, ItineraryDayRegistryEntry>;

/** The keys grouped by card, ordered by card then position. Pure. */
export function itineraryPlacements(
  registry: ItineraryRegistry = ITINERARY_RECORD_REGISTRY,
): Map<ItineraryRecordSection, ItineraryRecordFieldKey[]> {
  const index = (s: ItineraryRecordSection) => ITINERARY_RECORD_SECTIONS.indexOf(s);
  const keys = (Object.keys(registry) as ItineraryRecordFieldKey[]).sort((a, b) => {
    const x = registry[a];
    const y = registry[b];
    return index(x.stage) - index(y.stage) || x.order - y.order;
  });
  const out = new Map<ItineraryRecordSection, ItineraryRecordFieldKey[]>();
  for (const key of keys) {
    const section = registry[key].stage;
    const list = out.get(section);
    if (list) list.push(key);
    else out.set(section, [key]);
  }
  return out;
}

function catalogField(
  entity: string,
  key: string,
  e: { label: string; kind: ItineraryFieldKind; widget: string | null; choices?: readonly RecordChoice[] },
): RecordField {
  return {
    key,
    entity,
    label: e.label,
    kind: e.kind,
    // The core's input for the catalog's widget name (`coreWidget`) — as the catalog's field.
    widget: coreWidget(e.widget),
    choices: [...(e.choices ?? [])],
    guards: [],
    permissions: [],
    follows_lead: false,
    effects: [],
    automatic: [],
    restech_rule: null,
  };
}

/** The registry as the records core's catalog (`RecordEditorConfig.fields`), in card order:
 *  what the FieldSlots read until the server's edit catalog arrives — the same labels, kinds
 *  and inputs (`coreWidget`) as the catalog's (the contract test), so nothing moves when it
 *  takes over. The day keys are
 *  `itineraryDayRecordFields` — two of them (`description`, `footer`) share a key with an
 *  itinerary field, which the records core tells apart by entity (`fieldIndex`). */
export function itineraryRecordFields(registry: ItineraryRegistry = ITINERARY_RECORD_REGISTRY): RecordField[] {
  return [...itineraryPlacements(registry).values()].flat().map((key) => catalogField(ITINERARY_ENTITY, key, registry[key]));
}

/** A day's keys as catalog fields of the `day` entity, in `ItineraryDayRecordFieldKey` order —
 *  the Days card's open day. */
export function itineraryDayRecordFields(): RecordField[] {
  return (Object.keys(ITINERARY_DAY_REGISTRY) as ItineraryDayRecordFieldKey[])
    .sort((a, b) => ITINERARY_DAY_REGISTRY[a].order - ITINERARY_DAY_REGISTRY[b].order)
    .map((key) => catalogField(ITINERARY_DAY_ENTITY, key, { ...ITINERARY_DAY_REGISTRY[key], kind: 'editable' }));
}
